import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../apiClient';
import { listOfferings } from '../../api/offeringsApi';
import { getActiveTenant } from '../../helpers/tenantHelpers';
import './OnboardingChecklist.css';

/* ONBOARDING CHECKLIST — what a new office still has to do, computed.

   EVERY STEP IS COMPUTED FROM REAL CONFIG, never from a "setup_complete" flag.
   A flag records that someone clicked through a wizard; it does not record
   whether the office can actually take a booking. t27 (RT's Ranch, events) is
   the case that motivated this: branding, portal, identity, notifications and
   outreach are all configured, 27 leads and 6 quotes are in, and yet the office
   has ZERO service offerings — so "New Quote" has nothing to sell. No flag
   anywhere said that. Counting the rows did.

   "DO IT NOW" LINKS TO WORKING SCREENS ONLY. Each step names a route, and the
   route is checked against ROUTES — a list of destinations verified to exist in
   App.js / SettingsShell at the time of writing. A step whose route is not in
   that list renders WITHOUT a button rather than with one that 404s. A dead
   "Do it now" is worse than none: it teaches an owner the checklist lies.

   This is NOT the same thing as FinishSetupBanner, which surfaces
   AI-INFERRED config awaiting a human's confirmation. This one is the
   never-configured list. They can both be true at once.

   It disappears entirely when every step is done — an onboarding list that
   lingers at 5/5 is decoration. */

/* Routes verified present: TOOL_META.catalog and TAB_META.{scheduling,team,
   portal} in pages/settings/v2/SettingsShell.jsx, and /customers in App.js.
   If a route is removed, delete it here too and the step loses its button
   rather than pointing at a hole. */
const ROUTES = {
  catalog: '/settings/catalog',
  scheduling: '/settings/scheduling',
  team: '/settings/team',
  portal: '/settings/portal',
  customers: '/customers',
};

const has = (obj, key) => !!(obj && typeof obj === 'object' && obj[key] != null);

/* Step lists per industry. The SHAPE is shared; only the wording and which
   steps apply differ, so a new vertical adds a list here and inherits the
   whole mechanism. `done` is a function of real fetched state — never a flag. */
function stepsFor(industry, state) {
  const { offeringCount, prefs, customerCount, teamCount, got } = state;
  const common = [
    { key: 'offerings', source: 'offerings', done: offeringCount > 0, route: ROUTES.catalog },
    { key: 'hours',     source: 'prefs',     done: has(prefs, 'business_hours'), route: ROUTES.scheduling },
    { key: 'team',      source: 'team',      done: teamCount > 1, route: ROUTES.team },
    { key: 'customers', source: 'customers', done: customerCount > 0, route: ROUTES.customers },
  ].filter((s) => got[s.source]);

  const WORDING = {
    events: {
      title: 'Finish setting up your venue',
      offerings: ['Add your packages', 'The packages and add-ons you sell. Quotes are built from these.'],
      hours: ['Set your booking hours', 'When you take events, so the calendar can stop double-booking.'],
      team: ['Invite your team', 'Coordinators and staff who need the calendar.'],
      customers: ['Add your first client', 'Or let an enquiry create one for you.'],
      portal: ['Brand your booking page', 'What a client sees when you send them a quote.'],
    },
    landscaping: {
      title: 'Finish setting up your yard',
      offerings: ['Add your services', 'Mowing, design, irrigation — what you sell, and what it costs.'],
      hours: ['Set your working hours', 'So routes and crews are scheduled inside them.'],
      team: ['Invite your crew', 'Everyone who needs a route on their phone.'],
      customers: ['Add your first property', 'Or let a lead create one for you.'],
      portal: ['Brand your customer page', 'What a customer sees on a quote or invoice.'],
    },
    general: {
      title: 'Finish setting up',
      offerings: ['Add your services', 'What you sell, and what it costs.'],
      hours: ['Set your working hours', 'So scheduling stays inside them.'],
      team: ['Invite your team', 'Everyone who needs access.'],
      customers: ['Add your first customer', 'Or let a lead create one for you.'],
      portal: ['Brand your customer page', 'What a customer sees on a quote.'],
    },
  };

  const words = WORDING[industry] || WORDING.general;

  const list = common.map((s) => ({
    ...s,
    label: words[s.key]?.[0] || s.key,
    hint: words[s.key]?.[1] || '',
  }));

  // Branding is only worth prompting where a client actually sees the page.
  if (industry === 'events' || industry === 'landscaping') {
    if (got.prefs) list.push({
      key: 'portal',
      done: has(prefs, 'branding'),
      route: ROUTES.portal,
      label: words.portal[0],
      hint: words.portal[1],
    });
  }

  return { title: words.title, list };
}

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const tenant = getActiveTenant();
  const industry = String(tenant?.industry || 'general').toLowerCase();

  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      // allSettled: one dead endpoint must not blank the whole checklist, and a
      // source that failed must not be read as "this step is done".
      const [offerings, prefs, customers, team] = await Promise.allSettled([
        listOfferings({ active: true }),
        api.get('/accounts/preferences/'),
        api.get('/customers/', { params: { page_size: 1 } }),
        // VERIFIED LIVE: /accounts/team/ answers 200 (t27 -> 2 rows).
        // /accounts/users/ and /tenant/users/ both 404 — TeamTab tries all
        // three and takes the first that works, which hid the fact that only
        // one exists. Calling a dead endpoint here would have left the team
        // count at 0 forever, so "Invite your team" could never complete.
        api.get('/accounts/team/'),
      ]);
      if (!live) return;

      const rows = (r) => {
        if (r.status !== 'fulfilled') return null;
        const d = r.value?.data ?? r.value;
        if (Array.isArray(d)) return d.length;
        if (typeof d?.count === 'number') return d.count;
        if (Array.isArray(d?.results)) return d.results.length;
        return null;
      };

      const offeringCount = rows(offerings);
      const customerCount = rows(customers);
      const teamCount = rows(team);
      const prefsObj =
        prefs.status === 'fulfilled'
          ? (prefs.value?.data?.preferences ?? prefs.value?.data ?? null)
          : null;

      // If we could not read the two sources that decide most steps, say
      // nothing rather than present an unknown as "not done".
      // A step whose source failed must not be shown as incomplete — an
      // unreadable count is unknown, not zero. Steps with a null source are
      // dropped from the list entirely further down.
      if (offeringCount === null && prefsObj === null) {
        setFailed(true);
        return;
      }
      setState({
        offeringCount,
        customerCount,
        teamCount,
        prefs: prefsObj,
        // Which sources answered. A step reading an unanswered source is
        // omitted rather than rendered as an outstanding task.
        got: {
          offerings: offeringCount !== null,
          customers: customerCount !== null,
          team: teamCount !== null,
          prefs: prefsObj !== null,
        },
      });
    })();
    return () => {
      live = false;
    };
  }, [tenant?.id]);

  const computed = useMemo(() => (state ? stepsFor(industry, state) : null), [industry, state]);

  if (failed || !computed) return null;

  const remaining = computed.list.filter((s) => !s.done);
  // Nothing left to do: the card goes away rather than sitting at 5/5.
  if (remaining.length === 0) return null;

  const doneCount = computed.list.length - remaining.length;

  return (
    <section className="onboard-card" aria-labelledby="onboard-title">
      <header className="onboard-head">
        <div>
          <h2 id="onboard-title" className="onboard-title">{computed.title}</h2>
          <p className="onboard-sub">
            {doneCount} of {computed.list.length} done
          </p>
        </div>
        <div className="onboard-meter" aria-hidden="true">
          <span style={{ width: `${(doneCount / computed.list.length) * 100}%` }} />
        </div>
      </header>

      <ul className="onboard-list">
        {computed.list.map((step) => (
          <li key={step.key} className={`onboard-step${step.done ? ' is-done' : ''}`}>
            <span className={`onboard-tick${step.done ? ' is-done' : ''}`} aria-hidden="true">
              {step.done ? '✓' : ''}
            </span>
            <span className="onboard-body">
              <span className="onboard-label">{step.label}</span>
              {!step.done && step.hint ? <span className="onboard-hint">{step.hint}</span> : null}
            </span>
            {/* No button on a done step, and none on a step whose destination
                is not a route we verified exists. */}
            {!step.done && step.route ? (
              <button type="button" className="onboard-do" onClick={() => navigate(step.route)}>
                Do it now
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
